import axiosRequest from "../universal/axiosService";
import { ENDPOINTS } from "../../apiConfig";

export const fetchIncentives = async ({
  limit,
  page,
  filters,
  sort,
  ret,
  ref,
  incl,
}) => {
  const commonParams = {
    coll: "Incentive",
    fil: JSON.stringify(filters),
  };
  const incentivesParams = {
    ...commonParams,
    lim: limit,
    pg: page,
    cache: false,
    sort: JSON.stringify(sort),
    ret,
    ref,
    incl,
  };

  const [incentivesResult, totalCountResult] = await Promise.all([
    axiosRequest({
      method: "GET",
      url: ENDPOINTS.INCENTIVES.GET_ALL,
      params: incentivesParams,
    }),
    axiosRequest({
      method: "GET",
      url: ENDPOINTS.INCENTIVES.GET_TOTAL_COUNT,
      params: commonParams,
    }),
  ]);

  if (!incentivesResult.success) throw new Error(incentivesResult.error);
  if (!totalCountResult.success) throw new Error(totalCountResult.error);

  return {
    data: incentivesResult.data.data || [],
    totalCount: totalCountResult.data.data || 0,
  };
};

export const fetchIncentiveSheet = async ({ module, status }) => {
  const result = await axiosRequest({
    method: "GET",
    url: ENDPOINTS.INCENTIVES.GET_SHEET,
    params: { module, status },
  });
  if (!result.success) throw new Error(result.error);
  return result.data.data || []; // Return the actual array
};

// Lightweight count-only fetch for card display
export const fetchIncentiveCount = async (filters) => {
  const result = await axiosRequest({
    method: "GET",
    url: ENDPOINTS.INCENTIVES.GET_TOTAL_COUNT,
    params: {
      coll: "Incentive",
      fil: JSON.stringify(filters),
    },
  });
  if (!result.success) return 0;
  const count = result.data;
  if (typeof count === 'number') return count;
  if (typeof count === 'object' && count !== null) return Number(count.data || count.count || 0);
  return Number(count) || 0;
};

export const fetchOneIncentive = async ({ incentiveUiid, retrieve, ref }) => {
  const params = {
    coll: "Incentive",
    ret: retrieve,
    ref,
    fil: JSON.stringify({ uiid: { type: "NONE", value: incentiveUiid } }),
  };
  const result = await axiosRequest({
    method: "GET",
    url: ENDPOINTS.INCENTIVES.GET_BY_UIID,
    params,
  });
  if (!result.success) throw new Error(result.error);
  return result.data.data;
};

export const addNewIncentive = async (incentiveData) => {
  return await axiosRequest({
    method: "POST",
    url: ENDPOINTS.INCENTIVES.ADD_NEW,
    data: {
      coll: "Incentive",
      payload: incentiveData,
    },
  });
};

export const addIncentivesBulk = async (incentiveArray) => {
  return await axiosRequest({
    method: "POST",
    url: ENDPOINTS.INCENTIVES.ADD_NEW,
    data: {
      coll: "Incentive",
      payload: incentiveArray,
      bulkPost: true,
    },
  });
};

export const updateIncentive = async ({ coll, uiid, payload, patchId, tPath }) => {
  const targetColl = coll || "Incentive";
  const params = {
    coll: targetColl,
    uiid,
    ...(patchId && { patchId }),
    ...(tPath && { tPath }),
  };
  
  const dynamicUrl = ENDPOINTS.INCENTIVES.UPDATE.replace(
    /\/incentives\/update/i, 
    `/${targetColl.toLowerCase()}s/update`
  );

  return await axiosRequest({
    method: "PATCH",
    url: dynamicUrl,
    params,
    data: { payload }, 
  });
};
export const deleteIncentive = async ({ incentiveUiid }) => {
  const result = await axiosRequest({
    method: "DELETE",
    url: ENDPOINTS.INCENTIVES.DELETE,
    data: {
      coll: "Incentive",
      uiid: Number(incentiveUiid),
    },
  });
  if (!result.success) throw new Error(result.error);
  return result.data;
};

export const fetchUsers = async ({
  source = "ats",
  limit,
  page,
  filters,
  sort,
  ret,
  ref,
  incl,
}) => {
  // Pick the correct userrefs endpoint based on source DB
  // ATS DB has RE, BDM, RA, DM, DL (role.module=2)
  // WF DB has Finance, HR (role.module=3)
  // Bench DB has Bench Recruiters (role.module=4)
  const urlMap = {
    ats:   { getAll: ENDPOINTS.USER_REFS.ATS_USER_GET_ALL, count: ENDPOINTS.USER_REFS.ATS_USER_COUNT },
    wf:    { getAll: ENDPOINTS.USER_REFS.WF_USER_GET_ALL,  count: ENDPOINTS.USER_REFS.WF_USER_COUNT },
    bench: { getAll: ENDPOINTS.USEREF.GET_ALL,             count: ENDPOINTS.USEREF.GET_TOTAL_COUNT },
  };
  const urls = urlMap[source] || urlMap.ats;

  const commonParams = {
    fil: JSON.stringify(filters),
  };
  const usersParams = {
    ...commonParams,
    lim: limit,
    pg: page,
    cache: false,
    sort: JSON.stringify(sort),
    ret,
    ref,
    incl,
  };

  const [usersResult, totalCountResult] = await Promise.all([
    axiosRequest({
      method: "GET",
      url: urls.getAll,
      params: usersParams,
    }),
    axiosRequest({
      method: "GET",
      url: urls.count,
      params: commonParams,
    }),
  ]);

  if (!usersResult.success) throw new Error(usersResult.error);
  if (!totalCountResult.success) throw new Error(totalCountResult.error);

  return {
    data: usersResult.data,
    totalCount: totalCountResult.data,
  };
};

export const fetchRoles = async ({
  limit,
  page,
  filters,
  sort,
  ret,
  ref,
  incl,
}) => {
    const params = {
        coll: "Role",
        fil: JSON.stringify(filters),
        lim: limit,
        pg: page,
        cache: false,
        sort: JSON.stringify(sort),
        ret,
        ref,
        incl,
    };

    const result = await axiosRequest({
        method: "GET",
        url: ENDPOINTS.ROLES.GET_ALL,
        params,
    });

    if (!result.success) throw new Error(result.error);
    return result.data.data || [];
};
export const fetchIncentiveSettings = async (key) => {
  const result = await axiosRequest({
    method: "GET",
    url: ENDPOINTS.INCENTIVE_SETTINGS.GET_BY_KEY,
    params: { key },
  });
  if (!result.success) throw new Error(result.error);
  return result.data.data;
};
